import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { api } from '../../api/client'

interface MoodCheckProps {
  onClose: () => void
  onSuccess?: (score: number) => void
}

const MOOD_OPTIONS = [
  { score: 1, emoji: '😢', label: 'Очень плохо' },
  { score: 2, emoji: '😕', label: 'Плохо' },
  { score: 3, emoji: '😐', label: 'Нормально' },
  { score: 4, emoji: '🙂', label: 'Хорошо' },
  { score: 5, emoji: '😊', label: 'Отлично' },
]

async function submitMood(data: { score: number }) {
  const response = await api.post('/user/mood', data)
  return response.data
}

export default function MoodCheck({ onClose, onSuccess }: MoodCheckProps) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: submitMood,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['moodHistory'] })
      onSuccess?.(variables.score)
      onClose()
    },
  })

  const handleScoreSelect = (score: number) => {
    // Single click = save and close
    mutation.mutate({ score })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-neutral-800">
              Как вы себя чувствуете?
            </h2>
            <button
              onClick={onClose}
              className="p-1 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex justify-center gap-3">
            {MOOD_OPTIONS.map((option) => (
              <button
                key={option.score}
                onClick={() => handleScoreSelect(option.score)}
                disabled={mutation.isPending}
                className={clsx(
                  'flex flex-col items-center gap-1 p-3 rounded-xl transition-all',
                  'hover:bg-neutral-100 hover:scale-110 active:scale-95',
                  mutation.isPending && 'opacity-50 cursor-not-allowed'
                )}
                title={option.label}
              >
                <span className="text-3xl">{option.emoji}</span>
                <span className="text-xs text-neutral-500">{option.label}</span>
              </button>
            ))}
          </div>

          {mutation.isError && (
            <p className="mt-4 text-sm text-error text-center">
              Ошибка сохранения. Попробуйте ещё раз.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
